/*
 * Based on
 * https://github.com/apache/guacamole-client/blob/d1e928bea79ca81c827e9b6adedabc98eefdf701/guacamole/src/main/webapp/app/client/services/guacImage.js
 *
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

/**
 * Map of possibly-supported image mimetypes to corresponding test images
 * encoded with base64. If the image is correctly decoded, it will be a
 * single pixel (1x1) image.
 */
const testImages = {
  /**
   * Test JPEG image, encoded as base64.
   */
  'image/jpeg': '/9j/4AAQSkZJRgABAQEASABIAAD/2wBDAAMCAgMCAgMDAwMEAwMEBQgFBQQEBQoH' + 'BwYIDAoMDAsKCwsNDhIQDQ4RDgsLEBYQERMUFRUVDA8XGBYUGBIUFRT/2wBDAQME' + 'BAUEBQkFBQkUDQsNFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQU' + 'FBQUFBQUFBQUFBQUFBT/wAARCAABAAEDAREAAhEBAxEB/8QAFAABAAAAAAAAAAA' + 'AAAAAAAAACf/EABQQAQAAAAAAAAAAAAAAAAAAAAD/xAAUAQEAAAAAAAAAAAAAAA' + 'AAAAAA/8QAFBEBAAAAAAAAAAAAAAAAAAAAAP/aAAwDAQACEQMRAD8AVMH/2Q==',
  /**
   * Test PNG image, encoded as base64.
   */
  'image/png': 'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABAQMAAAAl21bKAAAAA1BMVEX///+nxBvI' + 'AAAACklEQVQI12NgAAAAAgAB4iG8MwAAAABJRU5ErkJggg==',
  /**
   * Test WebP image, encoded as base64.
   */
  'image/webp': 'UklGRhoAAABXRUJQVlA4TA0AAAAvAAAAEAcQERGIiP4HAA=='
};
/**
 * Array of all promises associated with image tests.
 */
const pendingTests = Object.entries(testImages).map(function testImageSupport(_ref) {
  let [mimetype, data] = _ref;
  return new Promise(resolve => {
    // Attempt to load image
    const image = new Image();

    // Store as supported depending on whether load was successful
    function imageTestComplete() {
      // Image format is supported if successfully decoded
      if (image.width === 1 && image.height === 1) {
        resolve({
          mimetype,
          supported: true
        });
      } else {
        resolve({
          mimetype,
          supported: false
        });
      }
    }
    image.onload = imageTestComplete;
    image.onerror = imageTestComplete;
    image.src = "data:".concat(mimetype, ";base64,").concat(data);
  });
});

/**
 * Return a promise which resolves with to an array of image mimetypes
 * supported by the browser, once those mimetypes are known. The returned
 * promise is guaranteed to resolve successfully.
 */
export function getSupportedMimetypes() {
  // When all image tests are complete, resolve promise with list of
  // supported formats
  return Promise.all(pendingTests).then(results => {
    return results.filter(result => result.supported).map(result => result.mimetype);
  });
}